/**
 * The in-app path a signed-out visitor was headed to (e.g. `/cli-login?user_code=AB12`),
 * so sign-in can send them back there.
 *
 * The string is attacker-controlled: `LoginPage` renders in place at whatever URL the
 * visitor opened (`https://app.example.com//evil.com/x` has the pathname `//evil.com/x`),
 * and the `/login/mfa?returnTo=` query can be written by anyone. Both end up assigned to
 * `window.location.href`, so `sanitizeReturnPath` is the gate that keeps them on-site.
 * It refuses off-site shapes (and the bare home page), login/auth paths that would loop,
 * and anything with a hash, which is never carried. Mirrors `sanitizeReturnTo` in
 * `apps/api/src/auth/handoff.ts`, which stays the authority for the SSO path; keep them
 * in step (this one is stricter: it also refuses a `#`).
 */
export function sanitizeReturnPath(raw: string): string | null {
  // Printable ASCII only: URL parsers strip tab/newline, so `/<TAB>/evil.com` would become `//evil.com`.
  if (/[^\x21-\x7e]/.test(raw) || raw.includes('#')) return null;
  const pathname = raw.split('?')[0];
  if (!/^\/[^/\\]/.test(pathname)) return null;
  if (/^\/(login|auth)(\/|$)/.test(pathname)) return null;
  return raw;
}

/** The sanitized path and query of the current location, or null when there is nothing worth returning to. */
export function getLoginReturnTo(location: Pick<Location, 'pathname' | 'search'>): string | null {
  return sanitizeReturnPath(location.pathname + location.search);
}
